import { Phone, MessageCircle, ArrowRight } from "lucide-react";
import {
    buildWhatsAppLink,
    PHONE_NUMBER_TEL,
    PHONE_NUMBER_DISPLAY,
} from "../lib/contact";

const HERO_IMAGE =
    "https://images.pexels.com/photos/29813821/pexels-photo-29813821.jpeg";

export const Hero = () => {
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative min-h-[100svh] flex items-center overflow-hidden grain"
        >
            {/* Background image */}
            <div className="absolute inset-0 z-0">
                <img
                    src={HERO_IMAGE}
                    alt="Yellow tower crane against sky"
                    className="w-full h-full object-cover scale-105"
                    loading="eager"
                />
                {/* Gradient overlays for legibility */}
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />
            </div>

            {/* Decorative caution stripe */}
            <div className="absolute top-20 right-0 h-1.5 w-2/3 caution-stripes opacity-80 hidden md:block" />
            <div className="absolute bottom-0 left-0 right-0 h-1 caution-stripes opacity-90" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-32 md:py-40 w-full">
                <div className="max-w-4xl">
                    {/* Eyebrow */}
                    <div className="reveal flex items-center gap-3 mb-6">
                        <span className="w-10 h-[2px] bg-[#FFC107]" />
                        <span className="font-body text-xs sm:text-sm uppercase tracking-[0.32em] text-[#FFC107]">
                            Est. 2002 · Maharashtra
                        </span>
                    </div>

                    {/* Headline */}
                    <h1
                        data-testid="hero-headline"
                        className="reveal font-display text-[12vw] leading-[0.88] sm:text-7xl md:text-8xl lg:text-[8.5rem] uppercase text-white"
                        style={{ animationDelay: "0.1s" }}
                    >
                        Crane Rental{" "}
                        <span className="text-[#FFC107]">&amp;</span>
                        <br />
                        Heavy Lifting
                        <br />
                        <span className="text-neutral-500">Solutions.</span>
                    </h1>

                    {/* Sub */}
                    <p
                        className="reveal mt-8 max-w-2xl font-body text-base sm:text-lg text-neutral-300 leading-relaxed"
                        style={{ animationDelay: "0.25s" }}
                    >
                        Trusted for{" "}
                        <span className="text-white font-semibold">
                            20+ years
                        </span>{" "}
                        across Mumbai, Thane, Bhiwandi, Wada &amp; nearby. From
                        12-ton mobile cranes to 100+ ton heavy haul rigs — we
                        move what others can&apos;t.
                    </p>

                    {/* CTAs */}
                    <div
                        className="reveal mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
                        style={{ animationDelay: "0.4s" }}
                    >
                        <a
                            href={buildWhatsAppLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-primary-cta"
                            className="group inline-flex items-center justify-center gap-3 bg-[#FFC107] hover:bg-[#E0A800] text-black font-body font-bold text-sm sm:text-base uppercase tracking-[0.18em] px-7 py-5 transition-all duration-200 hover:-translate-y-0.5 focus-ring"
                        >
                            <MessageCircle size={20} strokeWidth={2.5} />
                            Get Quote on WhatsApp
                            <ArrowRight
                                size={18}
                                className="transition-transform duration-200 group-hover:translate-x-1"
                            />
                        </a>
                        <a
                            href={`tel:${PHONE_NUMBER_TEL}`}
                            data-testid="hero-secondary-cta"
                            className="inline-flex items-center justify-center gap-3 bg-transparent border border-white/30 hover:border-white hover:bg-white/5 text-white font-body font-bold text-sm sm:text-base uppercase tracking-[0.18em] px-7 py-5 transition-all duration-200 focus-ring"
                        >
                            <Phone size={18} strokeWidth={2.5} />
                            Call Now
                        </a>
                    </div>

                    {/* Inline phone */}
                    <div
                        className="reveal mt-8 flex items-center gap-3 text-neutral-400 font-body text-sm"
                        style={{ animationDelay: "0.55s" }}
                    >
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                        <span>
                            Available 24 / 7 ·{" "}
                            <a
                                href={`tel:${PHONE_NUMBER_TEL}`}
                                className="text-white hover:text-[#FFC107] transition-colors font-semibold"
                                data-testid="hero-phone-link"
                            >
                                {PHONE_NUMBER_DISPLAY}
                            </a>
                        </span>
                    </div>
                </div>
            </div>

            {/* Bottom-left side label */}
            <div className="hidden lg:flex absolute bottom-12 right-8 z-10 flex-col items-end gap-2 text-right">
                <span className="font-display text-6xl text-white/10 leading-none">
                    01
                </span>
                <span className="font-body text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                    Scroll
                    <br />
                    Lift / Move / Build
                </span>
            </div>
        </section>
    );
};
